import React, { useEffect, useRef, useState } from "react";

import { useForm } from "../useForm";

import SelectMenu from "../../controls/SelectMenu";
import {
  conditionTypes,
  convertToDefEventPara,
  orderStatusOptions,
  updateObjectInArray,
} from "../../../shared/utility";
import OrderItemsForShipping from "./OrderItemsForShipping";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import ProductTemperaturePromptModal from "./ProductTemperaturePromptModal";
import { classNames } from "primereact/utils";
import Input from "../../controls/Input";
import { Accordion, AccordionTab } from "primereact/accordion";
import DatePickerInput from "../../controls/DatePickerInput";
import OrderNotes from "../orders/modals/OrderNotes";

const initialFValues = {
  id: 0,
  customer_id: 0,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_id: 0,
  orderItems: [],
  product_temperature: 0,
  product_condition: { value: 1, label: "Satisfactory" },
  truck_temperature: 0,
  truck_condition: { value: 1, label: "Satisfactory" },
};

function normalizeShippingItemsFromBackendOrderItem(backendOrderItem) {
  if (!backendOrderItem) return [];
  if (Array.isArray(backendOrderItem.shippingItems)) {
    return backendOrderItem.shippingItems;
  }

  const shippedCartons = Array.isArray(backendOrderItem.shipped_cartons)
    ? backendOrderItem.shipped_cartons
    : [];

  return shippedCartons.map((carton) => {
    const packagingDate = carton.packaging_date ?? null;

    return {
      id: carton.id,
      barcode: carton.barcode,
      lot_number: carton.lot_number,
      packing_size: carton.packing_size,
      packaging_date: packagingDate,
      carton_count: carton.carton_count,
      carton_type: carton.carton_type,
      product_id: backendOrderItem.product_id,
      product_name: carton.product_name,
      plu: carton.plu,
      serial_number: carton.serial_number,
      best_before_date: carton.best_before_date,
      statustatus: carton.statustatus,
      description_en: carton.description_en,
      product_temperature: backendOrderItem.product_temperature,
      order_item_id: backendOrderItem.id,
      is_quick_scan: carton.is_quick_scan ?? false,
    };
  });
}

export default function CreateNewShippingForm(props) {
  const {
    shipping,
    setEmptyModelOpen,
    showErrorModal,
    onFetchPackagedItemByBarcode,
    updateShippingApiCall,
    addPackagedItemToOrderItem,
    removeShippedCartonApiCall,
    employeeOptions,
    deleteShipping,
    printShipping,
    createShippingRecordByID,
    setProductTemperature,
    getOrderItemShipments,
    user_type,
    generateAndPrintShippingLabel,
  } = props;

  const isAdminUser = parseInt(user_type, 10) === 1;

  const [barcodeScanInProgress, setBarcodeScanInProgress] = useState(false);

  const barcodeInputRef = useRef(null);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.order_id = null;
    if (values.order_id === 0) {
      temp.order_id = "Please fill out field.";
    }

    temp.shipped_date = null;
    if (!values.shipped_date) {
      temp.shipped_date = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  useEffect(() => {
    if (shipping) {
      const shippingCopy = {
        ...shipping,
        status: orderStatusOptions.filter(
          (option) => option.value == shipping.status,
        )[0],
      };
      setValues(shippingCopy);
    }
  }, [shipping]);

  function findIndexForOrderItem(product_id) {
    return values.orderItems.findIndex(
      (element) => element.product_id === product_id,
    );
  }

  const updateShipping = () => {
    if (validate()) {
      updateShippingApiCall(values);
    }
  };

  const createShipping = () => {
    updateShipping();
  };

  const normalItemScanned = async function (
    packagedItem,
    itemIndex,
  ) {
    const orderItems = [...values.orderItems];
    const orderItem = orderItems[itemIndex];

    const callResult = await addPackagedItemToOrderItem(
      values.id,
      orderItem,
      packagedItem,
    );

    if (!callResult?.success) return;

    const updatedOrderItem = callResult?.data?.order_item;
    if (!updatedOrderItem) return;

    const refreshedShippingItems =
      normalizeShippingItemsFromBackendOrderItem(updatedOrderItem);

    const orderItemBeingEdited = {
      ...orderItem,
      ...updatedOrderItem,
      shippingItems: refreshedShippingItems,
    };

    const payload = {
      index: itemIndex,
      item: orderItemBeingEdited,
    };
    const newOrderItems = updateObjectInArray(orderItems, payload);
    setValues({
      ...values,
      orderItems: newOrderItems,
      scanned_box_label: "",
    });
  };

  const handleBarcodeScan = async function (scannedBarcode) {
    setValues({
      ...values,
      scanned_box_label: "",
    });
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
    let callResult = await onFetchPackagedItemByBarcode(scannedBarcode);
    if (callResult.success) {
      const packagedItem = callResult.data;
      var prodIdToSearch = packagedItem.packaged_item.product_id;
      if (packagedItem.is_skid_label) {
        if (packagedItem.packaged_item.length > 0) {
          prodIdToSearch = packagedItem.packaged_item[0].product_id;
        } else {
          showErrorModal("No items for skid label");
          return;
        }
      }
      const itemIndex = findIndexForOrderItem(prodIdToSearch);

      if (itemIndex < 0) {
        showErrorModal("Item not in order");
        return;
      }

      const orderItem = values.orderItems[itemIndex];
      const productTemperatureNotSet =
        orderItem.product_temperature == null ||
        orderItem.product_temperature === undefined;

      if (productTemperatureNotSet) {
        setEmptyModelOpen({
          childComponent: (
            <ProductTemperaturePromptModal
              setOpen={setEmptyModelOpen}
              orderItem={orderItem}
              order_id={values.order_id}
              setProductTemperature={setProductTemperature}
              showErrorModal={showErrorModal}
              onConfirm={(temperature) => {
                handleProductTemperatureUpdated(itemIndex, temperature);
                normalItemScanned(
                  packagedItem.packaged_item,
                  itemIndex,
                );
              }}
            />
          ),
          open: true,
        });
      } else {
        normalItemScanned(packagedItem.packaged_item, itemIndex);
      }
    } else {
      showErrorModal("We could not find that item.");
    }
  };

  const makeRemoveShippedCartonApiCall = async function (carton) {
    const product_id = carton.product_id;

    const itemIndex = findIndexForOrderItem(product_id);

    if (itemIndex < 0) return;

    const onItemDeleted = await removeShippedCartonApiCall(carton);
    if (!onItemDeleted?.success) return;

    const orderItems = [...values.orderItems];

    const orderItem = orderItems[itemIndex];

    const updatedOrderItemFromBackend = onItemDeleted?.data?.order_item;
    if (!updatedOrderItemFromBackend) return;

    const normalizedShippingItems =
      normalizeShippingItemsFromBackendOrderItem(updatedOrderItemFromBackend);

    const orderItemBeingEdited = {
      ...orderItem,
      ...updatedOrderItemFromBackend,
      shippingItems: normalizedShippingItems,
    };

    var payload = {
      index: itemIndex,
      item: orderItemBeingEdited,
    };
    const newOrderItems = updateObjectInArray(orderItems, payload);
    setValues({ ...values, orderItems: newOrderItems });
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const handleProductTemperatureUpdated = (index, temperature) => {
    const updatedItems = values.orderItems.map((item, i) =>
      i === index ? { ...item, product_temperature: temperature } : item,
    );
    setValues({ ...values, orderItems: updatedItems });
  };

  const handleOrderItemExpand = async function (orderItem) {
    if (!getOrderItemShipments) return;
    const orderItemId = orderItem.id;
    const result = await getOrderItemShipments(orderItemId);
    if (result.success && result.data) {
      // Functional update avoids overwriting another row's shippingItems when
      // multiple expands complete out of order (stale closure after await).
      setValues((prev) => {
        const itemIndex = prev.orderItems.findIndex(
          (element) => element.id === orderItemId,
        );
        if (itemIndex < 0) return prev;
        const orderItems = [...prev.orderItems];
        const payload = {
          index: itemIndex,
          item: { ...orderItems[itemIndex], shippingItems: result.data },
        };
        const newOrderItems = updateObjectInArray(orderItems, payload);
        return { ...prev, orderItems: newOrderItems };
      });
    }
  };

  const askDeleteShippingItem = function (item) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => {
            makeRemoveShippedCartonApiCall(item);
          }}
          message={`Are you sure you want to remove ${item.product_name} - ${item.barcode}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return (
    <div className="grid grid-col-1 sm:grid-cols-3 gap-3 h-full">
      <div className="sm:col-span-2 card-custom p-3 order-2 sm:order-1">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-1">
          <div className="sm:col-span-3">
            <div className="space-x-2 mb-1">
              <label
                htmlFor="scanned_box_label"
                className={"text-sm block font-medium text-gray-900"}
              >
                Scan barcode
              </label>
            </div>
            <input
              placeholder="Scan box label"
              name="scanned_box_label"
              disabled={barcodeScanInProgress}
              ref={barcodeInputRef}
              onChange={handleInputChange}
              value={values.scanned_box_label}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleBarcodeScan(e.target.value);
              }}
              type="search"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>
        </div>
        <OrderItemsForShipping
          setProductTemperature={setProductTemperature}
          askDeleteShippingItem={askDeleteShippingItem}
          orderItems={values.orderItems}
          order_id={values.order_id?.value ?? values.order_id}
          onExpandRow={handleOrderItemExpand}
          onProductTemperatureUpdated={handleProductTemperatureUpdated}
          user_type={user_type}
          onPrintShippingLabel={generateAndPrintShippingLabel}
        />
      </div>
      <div className="space-y-5 order-1 sm:order-2">
        <Accordion activeIndex={0}>
          <AccordionTab header="Shipping Information">
            <div className="grid grid-cols-1 gap-4">
              <div className="card-custom p-3 space-y-2">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="product_condition"
                  options={conditionTypes}
                  value={values.product_condition}
                  title="Product Condition"
                  error={errors.product_condition}
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="truck_condition"
                  options={conditionTypes}
                  value={values.truck_condition}
                  title="Truck Condition"
                  error={errors.truck_condition}
                />

                <Input
                  label="Truck Temperature"
                  name="truck_temperature"
                  onChange={handleInputChange}
                  value={values.truck_temperature}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.truck_temperature}
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="status"
                  options={orderStatusOptions.filter(
                    (option) => option.value !== -1,
                  )}
                  value={values.status}
                  title={"Status"}
                  error={errors.status}
                  showLabel={true}
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="created_by"
                  options={employeeOptions}
                  value={values.created_by}
                  title={"Employee"}
                  error={errors.created_by}
                  showLabel={true}
                />

                <DatePickerInput
                  label="Shipped date"
                  name="shipped_date"
                  onChange={handleInputChange}
                  value={values.shipped_date}
                  labelOn={true}
                  type="text"
                  dateFormat="LLL dd, yyyy"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.shipped_date}
                />

                <Input
                  label="Invoice #"
                  name="invoice_number"
                  onChange={handleInputChange}
                  value={values.invoice_number}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.invoice_number}
                />

                <div className="flex flex-wrap md:flex-nowrap gap-2 py-4">
                  <button
                    type="button"
                    className={classNames(
                      values.id > 0 ? null : "hidden",
                      "w-full md:w-auto inline-flex justify-center rounded-md bg-gray-500 px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600",
                    )}
                    onClick={() => printShipping()}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="w-full md:w-auto inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={createShipping}
                  >
                    Save
                  </button>
                  {isAdminUser && values.quickbooks_invoice_id > 0 ? (
                    <a
                      href={`https://qbo.intuit.com/app/invoice?txnId=${values.quickbooks_invoice_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full md:w-auto inline-flex items-center justify-center rounded-lg bg-green-600 px-3.5 py-2 text-xs sm:text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-green-700 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                    >
                      View Invoice
                    </a>
                  ) : null}
                  {isAdminUser ? (
                    <button
                      type="button"
                      className={classNames(
                        values.id > 0 ? null : "hidden",
                        "w-full md:w-auto inline-flex justify-center rounded-md bg-red-500 px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600",
                      )}
                      onClick={deleteShipping}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                  onClick={() => createShippingRecordByID()}
                >
                  Print Shipping Record
                </button>
              </div>

              <div className="card-custom p-3 mt-3">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-700 font-semibold">Notes</p>
                  <button
                    onClick={() =>
                      setEmptyModelOpen({
                        childComponent: (
                          <OrderNotes
                            orderNote={values.notes}
                            confirmAction={(newNote) =>
                              handleInputChange(
                                convertToDefEventPara("notes", newNote),
                              )
                            }
                            setOpen={setEmptyModelOpen}
                          />
                        ),
                        open: true,
                      })
                    }
                    className="text-sm font-semibold main-purple"
                  >
                    Edit
                  </button>
                </div>
                <p className="text-sm text-gray-700 font-normal">
                  {values.notes ? values.notes : "No notes"}
                </p>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
    </div>
  );
}
